/**
 * Theme store — persists theme preference (light / dark / system) and color
 * palette selection, applying the appropriate CSS classes and custom properties
 * on <html> so Tailwind dark-mode utilities and palette colors activate.
 */

import { create } from "zustand";
import {
  applyPalette,
  resetPalette,
  DEFAULT_PALETTE_ID,
  PALETTES,
} from "@/config/palettes";

export type ThemeMode = "light" | "dark" | "system";

interface ThemeState {
  mode: ThemeMode;
  resolved: "light" | "dark";
  /** Active palette ID (e.g. "ocean-blue", "emerald", "violet") */
  paletteId: string;
  setMode: (mode: ThemeMode) => void;
  /** Set the active palette. Persists to localStorage and applies CSS variables. */
  setPalette: (paletteId: string) => void;
}

function getSystemTheme(): "light" | "dark" {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function resolve(mode: ThemeMode): "light" | "dark" {
  return mode === "system" ? getSystemTheme() : mode;
}

function applyTheme(resolved: "light" | "dark") {
  const root = document.documentElement;
  if (resolved === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
}

// ---------------------------------------------------------------------------
// Initialization from localStorage
// ---------------------------------------------------------------------------

const storedMode =
  (typeof localStorage !== "undefined"
    ? (localStorage.getItem("ftl-theme") as ThemeMode | null)
    : null) ?? "system";

const storedPalette =
  (typeof localStorage !== "undefined"
    ? localStorage.getItem("ftl-palette")
    : null) ?? DEFAULT_PALETTE_ID;

const initialResolved = resolve(storedMode);

// Apply immediately to avoid flash of unstyled content
if (typeof document !== "undefined") {
  applyTheme(initialResolved);
  // Apply palette if not the default (default values are already in CSS @theme)
  if (storedPalette !== DEFAULT_PALETTE_ID && PALETTES[storedPalette]) {
    applyPalette(storedPalette);
  }
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useThemeStore = create<ThemeState>((set) => ({
  mode: storedMode,
  resolved: initialResolved,
  paletteId: storedPalette,

  setMode: (mode) => {
    const resolved = resolve(mode);
    localStorage.setItem("ftl-theme", mode);
    applyTheme(resolved);
    set({ mode, resolved });
  },

  setPalette: (paletteId) => {
    const id = PALETTES[paletteId] ? paletteId : DEFAULT_PALETTE_ID;
    localStorage.setItem("ftl-palette", id);
    if (id === DEFAULT_PALETTE_ID) {
      resetPalette();
    } else {
      applyPalette(id);
    }
    set({ paletteId: id });
  },
}));

// ---------------------------------------------------------------------------
// OS theme listener
// ---------------------------------------------------------------------------

if (typeof window !== "undefined") {
  window
    .matchMedia("(prefers-color-scheme: dark)")
    .addEventListener("change", () => {
      const { mode } = useThemeStore.getState();
      if (mode === "system") {
        const resolved = getSystemTheme();
        applyTheme(resolved);
        useThemeStore.setState({ resolved });
      }
    });
}
